import React, { useState, useCallback, useMemo } from 'react'
import { Layout, Flex, Typography, Avatar, Button, message } from 'antd'
import { LockOutlined, CloseOutlined, UnlockOutlined } from '@ant-design/icons'
import { useSelector, useDispatch } from 'react-redux'
import { useIntl } from 'react-intl'
import { selectedOutletSelector, userSelector } from '@/redux/selectors'
import { setLockScreen } from '@/redux/cashierSlice'
import { useValidatePin } from '@/utils/queries'
import { POS_ONLINE_HEADER } from '@/utils/static'
import Clock from './Clock'
import styles from './style.module.scss'
import ResetPINOnModal from '../Settings/ResetPIN/ResetPINOnModal'

const { Content } = Layout
const { Text, Title } = Typography

const LockScreen = () => {
  const dispatch = useDispatch()
  const { formatMessage } = useIntl()

  const outlet = useSelector(selectedOutletSelector)
  const user = useSelector(userSelector)
  const [pin, setPin] = useState('')
  const [openResetPIN, setOpenResetPIN] = useState(false)

  const { mutate: validatePinMutation, isLoading: isValidatePinLoading } = useValidatePin()

  const handleOpenResetPIN = useCallback(() => {
    setOpenResetPIN(true)
  }, [])

  const handleCloseResetPIN = useCallback(() => {
    setOpenResetPIN(false)
  }, [])

  const handleNumberClick = useCallback(
    (num) => {
      if (pin.length < 6) {
        setPin((prev) => prev + num)
      }
    },
    [pin.length],
  )

  const handleClear = useCallback(() => {
    setPin('')
  }, [])

  const handleBackspace = useCallback(() => {
    setPin((prev) => prev.slice(0, -1))
  }, [])

  const handleUnlock = useCallback(() => {
    validatePinMutation(
      {
        id: user?.id,
        pin,
        apiOptions: POS_ONLINE_HEADER,
      },
      {
        onSuccess: (response) => {
          if (response?.data?.success) {
            dispatch(setLockScreen(false))
            setPin('')
          } else {
            message.error(response?.data?.message || 'PIN tidak valid')
            setPin('')
          }
        },
        onError: (error) => {
          // Jika API belum tersedia, untuk sementara langsung unlock
          // Di production, harus ada API verify PIN yang proper
          if (error?.response?.status === 404) {
            // API belum tersedia, langsung unlock untuk development
            dispatch(setLockScreen(false))
            setPin('')
          } else {
            message.error(error?.response?.data?.message || 'PIN tidak valid')
            setPin('')
          }
        },
      },
    )
  }, [validatePinMutation, user?.id, pin, dispatch])

  const renderPinDots = useMemo(() => {
    const pinDots = [0, 1, 2, 3, 4, 5]
    return (
      <Flex gap={12} justify="center" style={{ marginBottom: 20 }}>
        {pinDots.map((dotPosition) => (
          <div
            key={`pin-dot-position-${dotPosition}`}
            style={{
              width: 14,
              height: 14,
              borderRadius: '50%',
              backgroundColor: dotPosition < pin.length ? '#1890ff' : 'rgba(255, 255, 255, 0.2)',
              transition: 'background-color 0.2s',
              border: dotPosition < pin.length ? 'none' : '1px solid rgba(255, 255, 255, 0.3)',
            }}
          />
        ))}
      </Flex>
    )
  }, [pin])

  const renderKeypad = useMemo(() => {
    const numbers = [
      { id: 'row-1', values: [1, 2, 3] },
      { id: 'row-2', values: [4, 5, 6] },
      { id: 'row-3', values: [7, 8, 9] },
    ]

    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        {numbers.map((row) => (
          <Flex key={row.id} gap={12} justify="center">
            {row.values.map((num) => (
              <Button
                key={num}
                type="text"
                size="large"
                onClick={() => handleNumberClick(num.toString())}
                style={{
                  width: 70,
                  height: 70,
                  fontSize: 24,
                  fontWeight: 500,
                  color: '#fff',
                  border: '1px solid rgba(255, 255, 255, 0.2)',
                  borderRadius: 8,
                  background: 'rgba(255, 255, 255, 0.05)',
                }}
              >
                {num}
              </Button>
            ))}
          </Flex>
        ))}
        <Flex gap={12} justify="center">
          <Button
            type="text"
            size="large"
            onClick={handleClear}
            style={{
              width: 70,
              height: 70,
              fontSize: 14,
              fontWeight: 500,
              color: '#fff',
              border: '1px solid rgba(255, 255, 255, 0.2)',
              borderRadius: 8,
              background: 'rgba(255, 255, 255, 0.05)',
            }}
          >
            CLEAR
          </Button>
          <Button
            type="text"
            size="large"
            onClick={() => handleNumberClick('0')}
            style={{
              width: 70,
              height: 70,
              fontSize: 24,
              fontWeight: 500,
              color: '#fff',
              border: '1px solid rgba(255, 255, 255, 0.2)',
              borderRadius: 8,
              background: 'rgba(255, 255, 255, 0.05)',
            }}
          >
            0
          </Button>
          <Button
            type="text"
            size="large"
            onClick={handleBackspace}
            style={{
              width: 70,
              height: 70,
              fontSize: 20,
              fontWeight: 500,
              color: '#fff',
              border: '1px solid rgba(255, 255, 255, 0.2)',
              borderRadius: 8,
              background: 'rgba(255, 255, 255, 0.05)',
            }}
          >
            <CloseOutlined />
          </Button>
        </Flex>
      </div>
    )
  }, [handleBackspace, handleClear, handleNumberClick])

  return (
    <Layout style={{ height: '100vh', background: '#1a1a1a', overflow: 'hidden' }}>
      <Content style={{ display: 'flex', height: '100vh', overflow: 'hidden' }}>
        {/* Left Side - Background dengan Logo dan Text */}
        <div
          style={{
            flex: 1,
            position: 'relative',
            backgroundImage:
              'url(https://lh3.googleusercontent.com/aida-public/AB6AXuC6P9G0gxhtS5IbKw77I8i836mYlvregkoSdwKGLk9cd8sojvhbu0jwc_lTTgoMssE7Iaj3ChXFj9V543eK0T-kgDhCo-ZGNtMZ7IRWfORYxh0fIlQTjbY3zQFC5KUBUfC_ofwwmbb1ZDcpW1AW1NTGcEypp5V3VkA6x-9sabjfM5FT4lx8e5ufUA0KuqwPFMJPPFPkMbhNZHfAH4gJMp4cZS8zX5QyzPnjg_lvvdRNTtblXJnPlG6gEpq5xrpZ-odZnjFVhh9MOjmx)',
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'space-between',
            padding: 48,
            height: '100vh',
            overflow: 'hidden',
          }}
        >
          <div
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: 'rgba(16, 22, 34, 0.4)',
              backgroundImage: 'linear-gradient(to top, rgba(16, 22, 34, 0.9), transparent)',
            }}
          />
          {/* Logo di atas */}
          <div style={{ position: 'relative', zIndex: 1 }}>
            <img
              src="resources/images/logo-pos-color.png"
              alt="POS Logo"
              style={{ height: 40, marginBottom: 24 }}
            />
          </div>

          {/* Jam dan Tanggal di tengah */}
          <Clock />

          {/* Text di bawah */}
          <div style={{ position: 'relative', zIndex: 1, color: '#fff' }}>
            <Title level={2} style={{ color: '#fff', marginBottom: 16, fontWeight: 600 }}>
              Secure, Fast, & Reliable Point of Sale.
            </Title>
            <Text style={{ color: 'rgba(255, 255, 255, 0.8)', fontSize: 16 }}>
              Ready to start your shift? Enter your secure PIN to access the terminal.
            </Text>
          </div>
        </div>

        {/* Right Side - Login Interface */}
        <div
          style={{
            flex: 1,
            background: '#1a1a1a',
            display: 'flex',
            flexDirection: 'column',
            padding: 48,
            justifyContent: 'center',
            alignItems: 'center',
            height: '100vh',
            overflow: 'hidden',
          }}
        >
          {/* Status Bar */}
          <Flex justify="space-between" align="center" style={{ width: '100%', marginBottom: 48 }}>
            <Text style={{ color: '#fff', fontSize: 16, fontWeight: 500 }}>Outlet</Text>
            <Text style={{ color: '#4777f3', fontSize: 16, fontWeight: 500 }}>{outlet?.name}</Text>
          </Flex>

          {/* User Profile */}
          <div style={{ textAlign: 'center', marginBottom: 20 }}>
            <div style={{ position: 'relative', display: 'inline-block', marginBottom: 16 }}>
              <Avatar
                size={120}
                src={
                  user?.profile_image ||
                  `https://ui-avatars.com/api/?name=${
                    user?.name || 'User'
                  }&background=1890ff&color=fff`
                }
              />
              <div
                style={{
                  position: 'absolute',
                  bottom: 0,
                  right: 0,
                  width: 32,
                  height: 32,
                  borderRadius: '50%',
                  background: '#ff9800',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  border: '2px solid #1a1a1a',
                }}
              >
                <LockOutlined style={{ color: '#fff', fontSize: 16 }} />
              </div>
            </div>
            <Title level={2} style={{ color: '#fff', marginBottom: 0, fontWeight: 600 }}>
              {user?.name || 'User'}
            </Title>
          </div>
          {/* PIN Input Dots */}
          {renderPinDots}
          {/* Keypad */}
          <div style={{ marginBottom: 32 }}>{renderKeypad}</div>
          {/* Unlock Button */}
          <div style={{ width: '100%', maxWidth: 400, marginBottom: 24 }}>
            <Button
              type="primary"
              size="large"
              icon={
                <UnlockOutlined
                  style={{ color: pin.length !== 6 ? 'rgba(255, 255, 255, 0.8)' : '#fff' }}
                />
              }
              onClick={handleUnlock}
              loading={isValidatePinLoading}
              disabled={pin.length !== 6}
              className={styles.unlockButton}
              block
              styles={{
                root: {
                  height: 56,
                  minHeight: 56,
                  fontSize: 16,
                  fontWeight: 700,
                  borderRadius: 12,
                  background: pin.length !== 6 ? 'rgba(33, 118, 255, 0.4)' : '#2176FF',
                  borderColor: pin.length !== 6 ? 'rgba(33, 118, 255, 0.4)' : '#2176FF',
                  boxShadow: pin.length !== 6 ? 'none' : '0 4px 12px rgba(33, 118, 255, 0.3)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 8,
                  padding: '0 24px',
                  color: pin.length !== 6 ? 'rgba(255, 255, 255, 0.8)' : '#fff',
                  '&:disabled': {
                    color: 'rgba(255, 255, 255, 0.8) !important',
                    background: 'rgba(33, 118, 255, 0.4) !important',
                    borderColor: 'rgba(33, 118, 255, 0.4) !important',
                    opacity: 1,
                    '& *': {
                      color: 'rgba(255, 255, 255, 0.8) !important',
                      opacity: 1,
                    },
                  },
                },
                icon: {
                  color: pin.length !== 6 ? 'rgba(255, 255, 255, 0.8)' : '#fff',
                },
              }}
            >
              <span style={{ color: pin.length !== 6 ? 'rgba(255, 255, 255, 0.8)' : '#fff' }}>
                {formatMessage({ id: 'open_cashier' })}
              </span>
            </Button>
          </div>
          {/* Footer Links */}
          <Flex gap={24} justify="center">
            <Button type="link" style={{ color: '#999', padding: 0 }} onClick={handleOpenResetPIN}>
              {`${formatMessage({ id: 'forgot_pin' })}?`}
            </Button>
            {/* <Button
              type="link"
              icon={<SwapOutlined />}
              style={{ color: '#999', padding: 0 }}
              onClick={handleSwitchUser}
            >
              Switch User
            </Button> */}
          </Flex>
        </div>
      </Content>
      {openResetPIN && <ResetPINOnModal open={openResetPIN} onCancel={handleCloseResetPIN} />}
    </Layout>
  )
}

export default LockScreen
